/**
 * Professional Order Entry Modal
 * Streamlined trading interface with intelligent defaults and risk management
 */

import { useState, useEffect, memo } from 'react'
import { useTradingStore } from '@/stores/useTradingStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { handleError, handleSuccess } from '@/lib/errorHandler'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Card, CardContent } from './ui/card'
import { LoadingSpinner } from './ui/loading-spinner'
import { 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Target, 
  Shield,
  Zap,
  BarChart3,
  Calculator,
  Globe
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import type { Instrument, OrderRequest, OrderDirection, OrderType } from '@/types'

interface OrderEntryModalProps {
  open: boolean
  onClose: () => void
  instrument?: Instrument & { 
    quickDirection?: OrderDirection
    tradeIntent?: {
      size?: number
      leverage?: number
      stopLoss?: number
      takeProfit?: number
    }
  }
}

export const OrderEntryModal = memo(function OrderEntryModal({ open, onClose, instrument }: OrderEntryModalProps) {
  const { settings } = useAuthStore()
  const { executeMarketOrder, executeLimitOrder, validateOrder, isLoading } = useTradingStore()

  const [orderType, setOrderType] = useState<OrderType>('MARKET')
  const [direction, setDirection] = useState<OrderDirection>('BUY')
  const [size, setSize] = useState('')
  const [maxLeverage, setMaxLeverage] = useState(200) // Default, will be updated
  const [limitPrice, setLimitPrice] = useState('')
  const [stopLoss, setStopLoss] = useState('')
  const [takeProfit, setTakeProfit] = useState('')
  
  const [validation, setValidation] = useState<{
    valid: boolean
    error?: string
    fees: number
    availableBalance: number
    aiInsights?: any
  } | null>(null)
  const [isValidating, setIsValidating] = useState(false)

  // Smart defaults and quick setup
  useEffect(() => {
    if (instrument) {
      // Fetch max leverage for this instrument
      const fetchMaxLeverage = async () => {
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL || 'https://trade-api.owethu.cloud/api'}/trades/leverage/${instrument.epic}`,
            {
              headers: {
                'X-Session-ID': localStorage.getItem('sessionId') || '',
              },
            }
          )
          if (response.ok) {
            const data = await response.json()
            if (data.success) {
              setMaxLeverage(data.data.maxLeverage)
            }
          }
        } catch (error) {
          console.error('Failed to fetch max leverage:', error)
        }
      }
      fetchMaxLeverage()
      
      // Set direction from quick trade if provided
      if (instrument.quickDirection) {
        setDirection(instrument.quickDirection)
      }
      
      // Use trade intent values if provided, otherwise use smart defaults
      const tradeIntent = instrument.tradeIntent
      
      // Smart size defaults - use instrument's minimum lot size
      const defaultSize = tradeIntent?.size || instrument.minDealSize || 0.01
      setSize(defaultSize.toString())
      
      // Leverage intent is ignored as it is fixed by broker
      
      // Smart price defaults
      setLimitPrice(instrument.offer.toString())
      
      // Smart risk management defaults
      const currentPrice = direction === 'BUY' ? instrument.offer : instrument.bid
      
      if (tradeIntent?.stopLoss && tradeIntent?.takeProfit) {
        // Use trade intent stop loss and take profit if provided
        setStopLoss(tradeIntent.stopLoss.toString())
        setTakeProfit(tradeIntent.takeProfit.toString())
      } else {
        // Calculate smart defaults
        const stopDistance = currentPrice * 0.02 // 2% stop loss
        const profitDistance = currentPrice * 0.04 // 4% take profit (2:1 ratio)
        
        if (direction === 'BUY') {
          setStopLoss((currentPrice - stopDistance).toFixed(5))
          setTakeProfit((currentPrice + profitDistance).toFixed(5))
        } else {
          setStopLoss((currentPrice + stopDistance).toFixed(5))
          setTakeProfit((currentPrice - profitDistance).toFixed(5))
        }
      }
    }
  }, [instrument, direction, settings])

  // Validate order with debouncing
  useEffect(() => {
    if (!instrument || !size || parseFloat(size) <= 0) {
      setValidation(null)
      return
    }

    const timer = setTimeout(async () => {
      setIsValidating(true)
      try {
        const order: OrderRequest = {
          instrument: instrument.epic,
          direction,
          type: orderType,
          size: parseFloat(size),
          leverage: maxLeverage,
          limitPrice: limitPrice ? parseFloat(limitPrice) : undefined,
          stopLoss: stopLoss ? parseFloat(stopLoss) : undefined,
          takeProfit: takeProfit ? parseFloat(takeProfit) : undefined,
        }
        const result = await validateOrder(order)
        setValidation(result)
      } catch (error) {
        console.error('Validation error:', error)
      } finally {
        setIsValidating(false)
      }
    }, 1000)

    return () => clearTimeout(timer)
  }, [instrument, size, maxLeverage, direction, orderType, limitPrice, stopLoss, takeProfit, validateOrder])

  const handleConfirm = async () => {
    if (!instrument || !validation?.valid) return

    const order: OrderRequest = {
      instrument: instrument.epic,
      direction,
      type: orderType,
      size: parseFloat(size),
      leverage: maxLeverage, // Backend still expects leverage, pass max Leverage
      limitPrice: limitPrice ? parseFloat(limitPrice) : undefined,
      stopLoss: stopLoss ? parseFloat(stopLoss) : undefined,
      takeProfit: takeProfit ? parseFloat(takeProfit) : undefined,
    }

    try {
      const result = orderType === 'MARKET'
        ? await executeMarketOrder(order)
        : await executeLimitOrder(order)

      if (result.success) {
        handleSuccess(`${direction} ${size} ${instrument.epic}`)
        onClose()
      } else {
        handleError(new Error(result.reason || 'Unable to execute order'))
      }
    } catch (error: any) {
      handleError(error)
    }
  }

  const calculateRiskReward = () => {
    if (!stopLoss || !takeProfit || !instrument) return null
    
    const currentPrice = direction === 'BUY' ? instrument.offer : instrument.bid
    const stopDistance = Math.abs(currentPrice - parseFloat(stopLoss))
    const profitDistance = Math.abs(parseFloat(takeProfit) - currentPrice)
    
    return profitDistance / stopDistance
  }

  const calculateProfitLoss = () => {
    if (!instrument || !size) return null
    
    const currentPrice = direction === 'BUY' ? instrument.offer : instrument.bid
    const positionSize = parseFloat(size)
    let stopLossAmount = 0
    let takeProfitAmount = 0

    if (stopLoss) {
      const stopPrice = parseFloat(stopLoss)
      const stopDistance = Math.abs(currentPrice - stopPrice)
      stopLossAmount = stopDistance * positionSize
    }
    
    if (takeProfit) {
      const tpPrice = parseFloat(takeProfit)
      const tpDistance = Math.abs(tpPrice - currentPrice)
      takeProfitAmount = tpDistance * positionSize
    }
    
    return {
      stopLossAmount,
      takeProfitAmount,
      stopLossPrice: stopLoss ? parseFloat(stopLoss) : null,
      takeProfitPrice: takeProfit ? parseFloat(takeProfit) : null,
    }
  }

  const riskReward = calculateRiskReward()
  const profitLossCalc = calculateProfitLoss()

  if (!instrument) return null

  const isLive = settings?.environment === 'live'
  const canSubmit = validation?.valid && !isLoading && !isValidating

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[95vh] overflow-y-auto bg-background border-border">
        <DialogHeader className="pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                <Zap className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <DialogTitle className="text-xl text-foreground">Place Order</DialogTitle>
                <DialogDescription className="flex items-center gap-2 mt-1 text-muted-foreground">
                  <Globe className="h-4 w-4" />
                  {instrument.name} ({instrument.epic})
                  <Badge variant="outline" className="ml-2">
                    {formatCurrency(instrument.offer)}
                  </Badge>
                  {isLive && <Badge variant="destructive" className="animate-pulse">LIVE</Badge>}
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Order Type & Direction */}
            <Card className="bg-muted/30 border-border">
              <CardContent className="p-4 space-y-4">
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-foreground">Order Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant={orderType === 'MARKET' ? 'default' : 'outline'}
                      onClick={() => setOrderType('MARKET')}
                      className="h-11"
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Market
                    </Button>
                    <Button
                      variant={orderType === 'LIMIT' ? 'default' : 'outline'}
                      onClick={() => setOrderType('LIMIT')}
                      className="h-11"
                    >
                      <Target className="h-4 w-4 mr-2" />
                      Limit
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-semibold text-foreground">Direction</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant={direction === 'BUY' ? 'default' : 'outline'}
                      onClick={() => setDirection('BUY')}
                      className={`h-11 ${direction === 'BUY' ? 'bg-profit hover:bg-profit/90 text-profit-foreground' : 'hover:bg-accent/10'}`}
                    >
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Buy
                    </Button>
                    <Button
                      variant={direction === 'SELL' ? 'default' : 'outline'}
                      onClick={() => setDirection('SELL')}
                      className={`h-11 ${direction === 'SELL' ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground' : 'hover:bg-destructive/10'}`}
                    >
                      <TrendingDown className="h-4 w-4 mr-2" />
                      Sell
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Position Sizing */}
            <Card className="bg-muted/30 border-border">
              <CardContent className="p-4 space-y-4">
                <div className="space-y-3">
                  <label htmlFor="size" className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Calculator className="h-4 w-4" />
                    Position Size (Lots)
                  </label>
                  <Input
                    id="size"
                    type="number"
                    step={instrument.minDealSize || 0.01}
                    min={instrument.minDealSize || 0.01}
                    placeholder={instrument.minDealSize?.toString() || "0.01"}
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className="h-11 text-lg font-semibold bg-background border-border"
                  />
                  <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded space-y-1">
                    <div className="flex justify-between">
                      <span>Minimum:</span>
                      <span className="font-semibold">{instrument.minDealSize} lots</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Maximum:</span>
                      <span className="font-semibold">{instrument.maxDealSize} lots</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Recommended:</span>
                      <span className="font-semibold">{instrument.minDealSize} - {Math.min(instrument.minDealSize * 5, instrument.maxDealSize)} lots</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-primary">Fixed Leverage</div>
                    <Badge variant="outline" className="border-primary/50 text-primary">
                      {maxLeverage}:1
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Capital.com assigns a fixed maximum leverage based on the instrument type.
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-primary/10 flex items-center justify-between">
                    <div className="text-sm font-semibold text-foreground">Required Margin</div>
                    <div className="text-sm font-bold text-primary">
                      {formatCurrency((parseFloat(size) || 0) * (direction === 'BUY' ? instrument.offer : instrument.bid) / maxLeverage)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Limit Price (for limit orders) */}
          {orderType === 'LIMIT' && (
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <div className="space-y-3">
                  <label htmlFor="limitPrice" className="text-sm font-semibold text-primary flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Limit Price
                  </label>
                  <Input
                    id="limitPrice"
                    type="number"
                    step="0.00001"
                    placeholder={instrument.offer.toString()}
                    value={limitPrice}
                    onChange={(e) => setLimitPrice(e.target.value)}
                    className="h-11 text-lg font-semibold bg-background border-border"
                  />
                  <div className="text-xs text-primary/80">
                    Current market price: {formatCurrency(instrument.offer)}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Risk Management */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="font-semibold text-foreground">Risk Management</div>
                  <div className="text-xs text-muted-foreground">
                    Protect your capital with stop loss and take profit levels
                  </div>
                </div>
                {riskReward && (
                  <Badge 
                    variant={riskReward >= 2 ? 'default' : riskReward >= 1 ? 'outline' : 'destructive'}
                    className="ml-auto"
                  >
                    R:R {riskReward.toFixed(1)}:1
                  </Badge>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Stop Loss */}
                <div className="space-y-2">
                  <label htmlFor="stopLoss" className="text-sm font-medium text-destructive flex items-center gap-2">
                    <TrendingDown className="h-4 w-4" />
                    Stop Loss Price
                  </label>
                  <Input
                    id="stopLoss"
                    type="number"
                    step="0.00001"
                    placeholder="Optional protection"
                    value={stopLoss}
                    onChange={(e) => setStopLoss(e.target.value)}
                    className="h-10 bg-background border-border"
                  />
                  {profitLossCalc && profitLossCalc.stopLossAmount > 0 && (
                    <div className="p-2 bg-destructive/10 border border-destructive/20 rounded text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Price:</span>
                        <span className="font-semibold text-destructive">
                          {formatCurrency(profitLossCalc.stopLossPrice!)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Max Loss:</span>
                        <span className="font-bold text-destructive">
                          -{formatCurrency(profitLossCalc.stopLossAmount)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Take Profit */}
                <div className="space-y-2">
                  <label htmlFor="takeProfit" className="text-sm font-medium text-profit flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Take Profit Price
                  </label>
                  <Input
                    id="takeProfit"
                    type="number"
                    step="0.00001"
                    placeholder="Optional target"
                    value={takeProfit}
                    onChange={(e) => setTakeProfit(e.target.value)}
                    className="h-10 bg-background border-border"
                  />
                  {profitLossCalc && profitLossCalc.takeProfitAmount > 0 && (
                    <div className="p-2 bg-profit/10 border border-profit/20 rounded text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Price:</span>
                        <span className="font-semibold text-profit">
                          {formatCurrency(profitLossCalc.takeProfitPrice!)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Profit:</span>
                        <span className="font-bold text-profit">
                          +{formatCurrency(profitLossCalc.takeProfitAmount)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Validation & Order Summary */}
          {isValidating && (
            <Card className="border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 text-primary">
                  <LoadingSpinner size="sm" />
                  <span className="text-sm">Validating order parameters...</span>
                </div>
              </CardContent>
            </Card>
          )}

          {validation && !isValidating && (
            <Card className={`${
              validation.valid 
                ? 'border-[hsl(142,76%,45%)]/20 bg-[hsl(142,76%,45%)]/5' 
                : 'border-destructive/20 bg-destructive/5'
            }`}>
              <CardContent className="p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex justify-between items-center p-3 bg-card rounded-lg border border-border">
                    <span className="text-muted-foreground">Available Balance</span>
                    <span className="font-bold text-foreground">
                      {formatCurrency(validation.availableBalance)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-card rounded-lg border border-border">
                    <span className="text-muted-foreground">Estimated Fees</span>
                    <span className="font-bold text-foreground">
                      {formatCurrency(validation.fees)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-card rounded-lg border border-border">
                    <span className="text-muted-foreground">Order Status</span>
                    <Badge variant={validation.valid ? 'default' : 'destructive'}>
                      {validation.valid ? 'Valid' : 'Invalid'}
                    </Badge>
                  </div>
                </div>
                
                {validation.aiInsights && (
                  <div className="p-4 bg-card rounded-lg border border-border">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                        <BarChart3 className="h-4 w-4 text-primary-foreground" />
                      </div>
                      <div className="font-medium text-foreground">AI Analysis</div>
                      <Badge variant={validation.aiInsights.agreesWithUser ? 'default' : 'outline'}>
                        {validation.aiInsights.agreesWithUser ? 'AI Agrees' : 'AI Suggests Caution'}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground mb-2">
                      Confidence: {validation.aiInsights.aiConfidence}%
                    </div>
                    {validation.aiInsights.aiReasoning && (
                      <div className="text-sm text-foreground italic">
                        "{validation.aiInsights.aiReasoning}"
                      </div>
                    )}
                  </div>
                )}
                
                {validation.error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <div className="text-destructive text-sm font-medium">
                      {validation.error}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Execute Order */}
          <div className="space-y-4">
            <Button
              onClick={handleConfirm}
              disabled={!canSubmit}
              className={`w-full h-14 text-lg font-semibold ${
                direction === 'BUY'
                  ? 'bg-profit hover:bg-profit/90 text-profit-foreground'
                  : 'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
              }`}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Executing Order...
                </>
              ) : (
                <>
                  {direction === 'BUY' ? <TrendingUp className="h-5 w-5 mr-2" /> : <TrendingDown className="h-5 w-5 mr-2" />}
                  {direction} {size} {instrument.epic}
                </>
              )}
            </Button>

            {isLive && (
              <div className="flex items-center justify-center gap-3 text-center p-4 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <div>
                  <div className="text-red-700 dark:text-red-300 font-semibold">Live Trading Environment</div>
                  <div className="text-red-600 dark:text-red-400 text-sm">
                    You are trading with real money. Please review your order carefully.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
})
